import { DestroyRef, Injectable, effect, inject, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { AnalyticsService } from './analytics.service';
import { ANALYTICS_EVENTS } from '../analytics/events';
import { BookingService } from './booking.service';
import { MapActionBusService } from './map-action-bus.service';
import { ProfileDataService } from './profile-data.service';
import { QuickBookService } from '../../shared/components/quick-book-dialog/quick-book.service';
import { trayPresence, type TrayPresence } from '../desktop-presence';
import { isTauri, onShellEvent } from '../tauri-runtime';

/** How often the countdown in the menu bar is recomputed. */
const TICK_MS = 60_000;
/** How often today's bookings are re-read while the app is resident. */
const REFRESH_MS = 5 * 60_000;

/**
 * DesktopTrayService — the menu-bar presence.
 *
 * RESPONSIBILITY: Keep the tray's title ("Podcast Room · in 12 min"), its
 * informational first row and the "Check in" item in step with today's
 * bookings, and run the tray's quick actions when Rust forwards them
 * (`tray:check-in`, `tray:quick-book`, `tray:report-issue`). The webview is
 * the source of truth; Rust only renders what `update_tray` is told.
 *
 * USED BY: AppShellComponent (starts it once the shell — and therefore a
 * session — exists). Desktop only; `start()` is a no-op on the web.
 *
 * AI CONTEXT: Today's bookings come from ProfileDataService, which the
 * profile page loads on demand; the tray loads and refreshes them itself so
 * the menu bar is right without the user ever visiting /profile.
 */
@Injectable({ providedIn: 'root' })
export class DesktopTrayService {
  private readonly profile = inject(ProfileDataService);
  private readonly booking = inject(BookingService);
  private readonly quickBook = inject(QuickBookService);
  private readonly actionBus = inject(MapActionBusService);
  private readonly router = inject(Router);
  private readonly analytics = inject(AnalyticsService);
  private readonly destroyRef = inject(DestroyRef);

  private started = false;
  private lastSent = '';
  private presence: TrayPresence | null = null;

  start(): void {
    if (!isTauri || this.started) return;
    this.started = true;

    void this.profile.loadCritical();
    const refresh = setInterval(() => void this.profile.refresh(), REFRESH_MS);
    const tick = setInterval(() => this.render(), TICK_MS);
    this.destroyRef.onDestroy(() => {
      clearInterval(refresh);
      clearInterval(tick);
    });

    // Re-render whenever today's bookings change (a new booking, a check-in).
    effect(() => {
      this.profile.meetingBookings();
      this.profile.deskBooking();
      untracked(() => this.render());
    });

    void onShellEvent('tray:check-in', () => void this.checkIn());
    void onShellEvent('tray:quick-book', () => this.openQuickBook());
    void onShellEvent('tray:report-issue', () => this.openIssueReport());
  }

  private render(): void {
    const next = trayPresence(this.profile.meetingBookings(), this.profile.deskBooking(), Date.now());
    this.presence = next;
    const payload = {
      title: next.title,
      nextLabel: next.nextLabel,
      checkInEnabled: next.checkInEnabled,
    };
    const serialised = JSON.stringify(payload);
    if (serialised === this.lastSent) return;
    this.lastSent = serialised;
    void import('@tauri-apps/api/core')
      .then(({ invoke }) => invoke('update_tray', payload))
      .catch((err) => console.warn('[desktop-tray] update_tray failed:', err));
  }

  private async checkIn(): Promise<void> {
    const id = this.presence?.checkInBookingId;
    this.analytics.capture(ANALYTICS_EVENTS.DESKTOP_TRAY_ACTION, { action: 'check-in' });
    if (!id) return;
    const ok = await this.booking.checkInBooking(id, 'tray', { source: 'tray' });
    if (ok) await this.profile.refresh();
  }

  private openQuickBook(): void {
    this.analytics.capture(ANALYTICS_EVENTS.DESKTOP_TRAY_ACTION, { action: 'quick-book' });
    void this.quickBook.start({ kind: 'desk', source: 'tray' });
  }

  /** The issue panel lives on the map, so go there first (same as the palette). */
  private openIssueReport(): void {
    this.analytics.capture(ANALYTICS_EVENTS.DESKTOP_TRAY_ACTION, { action: 'report-issue' });
    void this.router.navigateByUrl('/map').then(() => {
      setTimeout(() => this.actionBus.dispatch({ type: 'open-issue-report', context: { entry: 'tray' } }), 300);
    });
  }
}
